import { useState } from 'react';
import {
  ArrowLeftRight,
  BriefcaseBusiness,
  CheckCircle2,
  Clock3,
  KeyRound,
  ShieldAlert,
  UserRoundCheck,
  X,
} from 'lucide-react';
import {
  getDepartment,
  getDepartmentPeople,
  getPerson,
  getPersonJob,
  getPersonSession,
  type CompanySnapshot,
  type Department,
  type Person,
} from '@mk1/domain';
import {
  Panel,
  PanelHeader,
  PersonaAvatar,
  ProvenanceBadge,
  StatusPill,
  SyntheticBadge,
} from '@mk1/ui';

const statusTone = (status: string): 'cyan' | 'amber' | 'red' | 'green' | 'violet' | 'neutral' => {
  if (status === 'BLOCKED' || status === 'SUSPENDED') return 'red';
  if (status === 'WAITING_HUMAN' || status === 'ATTENTION') return 'amber';
  if (status === 'QA_REVIEW' || status === 'HEALTHY') return 'green';
  if (status === 'WAITING_TOOL' || status === 'COOLDOWN') return 'violet';
  if (status === 'OFFLINE') return 'neutral';
  return 'cyan';
};

function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(...values, 1);
  return (
    <span className="sparkline" role="img" aria-label={`Durchsatztrend ${values.join(', ')}`}>
      {values.map((value, index) => (
        <i key={`${value}-${index}`} style={{ height: `${Math.max(18, (value / max) * 100)}%` }} />
      ))}
    </span>
  );
}

function PersonRow({ person, onOpen }: { person: Person; onOpen: (id: string) => void }) {
  return (
    <button
      className="person-row"
      onClick={() => onOpen(person.id)}
      aria-label={`${person.name} öffnen`}
    >
      <PersonaAvatar name={person.name} nature={person.nature} status={person.status} compact />
      <span className="person-row__copy">
        <strong>{person.name}</strong>
        <small>{person.statusLabel}</small>
      </span>
      <StatusPill label={person.status.replace('_', ' ')} tone={statusTone(person.status)} />
    </button>
  );
}

function DepartmentCard({
  department,
  people,
  onDepartment,
  onPerson,
}: {
  department: Department;
  people: Person[];
  onDepartment: (id: string) => void;
  onPerson: (id: string) => void;
}) {
  return (
    <article className={`floor-room floor-room--${department.health.toLowerCase()}`}>
      <button className="floor-room__header" onClick={() => onDepartment(department.id)}>
        <span>
          <small>{department.shortName}</small>
          <strong>{department.objective}</strong>
        </span>
        <StatusPill label={department.health} tone={statusTone(department.health)} />
      </button>
      <div className="floor-room__people">
        {people.map((person) => (
          <PersonRow key={person.id} person={person} onOpen={onPerson} />
        ))}
      </div>
      <footer className="floor-room__footer">
        <span>
          <b>{department.queueCount}</b> Queue
          <small>ältestes {department.oldestItemHours.toLocaleString('de-DE')}h</small>
        </span>
        <span className="room-deadline">
          <Clock3 size={13} /> {department.nextDeadline}
        </span>
        <Sparkline values={department.throughput} />
      </footer>
    </article>
  );
}

export function CompanyFloor({
  snapshot,
  onDepartment,
  onPerson,
  compact = false,
}: {
  snapshot: CompanySnapshot;
  onDepartment: (id: string) => void;
  onPerson: (id: string) => void;
  compact?: boolean;
}) {
  const chaosCases = [
    ['heartbeat', 'RUNNER.HEARTBEAT_LOST'],
    ['duplicate', 'EVENT.DUPLICATE_IGNORED'],
    ['lease', 'TOOL_LEASE.EXPIRED'],
    ['dependency', 'DEPENDENCY.CANCELLED'],
    ['queue', 'QUEUE.PAUSED'],
    ['quota', 'CAPACITY.EXHAUSTED'],
    ['approval', 'GATE.STALE_REJECTED'],
    ['audit', 'AUDIT.STORE_UNAVAILABLE'],
    ['suspension', 'PERSON.SUSPENDED'],
  ] as const;
  return (
    <div className={compact ? 'company-floor is-compact' : 'company-floor'}>
      {snapshot.operations.companyDay.status !== 'NOT_RUN' ? (
        <div className="floor-chaos" aria-label="Synthetic failure containment">
          <span>
            <ShieldAlert size={14} /> SYNTHETIC_DEMO containment
          </span>
          {chaosCases.map(([label, eventType]) => (
            <i
              key={eventType}
              className={snapshot.events.some((event) => event.type === eventType) ? 'is-safe' : ''}
            >
              {label}
            </i>
          ))}
        </div>
      ) : null}
      <div className="floor-grid">
        {snapshot.departments.map((department) => (
          <DepartmentCard
            key={department.id}
            department={department}
            people={getDepartmentPeople(snapshot, department.id)}
            onDepartment={onDepartment}
            onPerson={onPerson}
          />
        ))}
      </div>
    </div>
  );
}

const departmentTabs = ['Now', 'Queue', 'People', 'Tools & Assets', 'Metrics'] as const;

export function DepartmentRoom({
  snapshot,
  departmentId,
  onClose,
  onPerson,
}: {
  snapshot: CompanySnapshot;
  departmentId: string;
  onClose: () => void;
  onPerson: (id: string) => void;
}) {
  const [tab, setTab] = useState<(typeof departmentTabs)[number]>('Now');
  const department = getDepartment(snapshot, departmentId);
  if (!department) return null;
  const people = getDepartmentPeople(snapshot, department.id);
  const jobs = snapshot.jobs.filter((job) => job.departmentId === department.id);

  return (
    <div
      className="overlay"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside
        className="drawer department-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="department-title"
      >
        <header className="drawer__hero">
          <div>
            <span className="ui-eyebrow">Department Room · {department.id}</span>
            <h2 id="department-title">{department.name}</h2>
            <p>{department.objective}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Department Room schließen">
            <X size={19} />
          </button>
        </header>
        <div className="drawer-kpis">
          <span>
            <small>Health</small>
            <b>{department.health}</b>
          </span>
          <span>
            <small>Queue</small>
            <b>{department.queueCount}</b>
          </span>
          <span>
            <small>Oldest</small>
            <b>{department.oldestItemHours.toLocaleString('de-DE')}h</b>
          </span>
          <span>
            <small>Next</small>
            <b>{department.nextDeadline}</b>
          </span>
        </div>
        <div className="tabs" role="tablist" aria-label="Department-Bereiche">
          {departmentTabs.map((item) => (
            <button
              key={item}
              role="tab"
              aria-selected={tab === item}
              className={tab === item ? 'is-active' : ''}
              onClick={() => setTab(item)}
            >
              {item}
            </button>
          ))}
        </div>
        <div className="drawer__body department-body" role="tabpanel">
          {tab === 'Now' ? (
            <>
              <div className="detail-block">
                <span className="ui-eyebrow">Active work</span>
                {jobs.length ? (
                  jobs.map((job) => (
                    <article className="job-line" key={job.id}>
                      <span className={`priority priority--${job.priority.toLowerCase()}`}>
                        {job.priority}
                      </span>
                      <div>
                        <strong>{job.title}</strong>
                        <small>
                          {job.id} · {job.family}
                        </small>
                      </div>
                      <StatusPill
                        label={job.status.replace('_', ' ')}
                        tone={statusTone(job.status)}
                      />
                    </article>
                  ))
                ) : (
                  <p className="empty-copy">Keine aktive Arbeit im synthetischen Snapshot.</p>
                )}
              </div>
              <div className="detail-block">
                <span className="ui-eyebrow">Current crew</span>
                {people.map((person) => (
                  <PersonRow key={person.id} person={person} onOpen={onPerson} />
                ))}
              </div>
            </>
          ) : null}
          {tab === 'Queue' ? (
            <div className="detail-block">
              <span className="ui-eyebrow">Ordered by priority → deadline → age</span>
              {jobs
                .toSorted((a, b) => a.priority.localeCompare(b.priority))
                .map((job, index) => (
                  <article className="queue-line" key={job.id}>
                    <b>{String(index + 1).padStart(2, '0')}</b>
                    <div>
                      <strong>{job.title}</strong>
                      <small>
                        {job.blockedReason ?? `Next owner: ${job.nextOwnerId ?? 'unassigned'}`}
                      </small>
                    </div>
                    <StatusPill
                      label={job.priority}
                      tone={job.priority === 'P0' ? 'red' : 'amber'}
                    />
                  </article>
                ))}
            </div>
          ) : null}
          {tab === 'People'
            ? people.map((person) => (
                <PersonRow key={person.id} person={person} onOpen={onPerson} />
              ))
            : null}
          {tab === 'Tools & Assets' ? (
            <div className="asset-grid">
              {department.tools.map((tool) => (
                <span key={tool}>
                  <KeyRound size={16} />
                  <b>{tool}</b>
                  <small>Metadata / preview only</small>
                </span>
              ))}
            </div>
          ) : null}
          {tab === 'Metrics' ? (
            <div className="metrics-detail">
              <div>
                <b>Queue age</b>
                <strong>{department.oldestItemHours.toLocaleString('de-DE')}h</strong>
                <small>
                  {snapshot.source.mode === 'LIVE_LOCAL'
                    ? 'database projection'
                    : 'synthetic observation'}
                </small>
              </div>
              <div>
                <b>SLA health</b>
                <strong>
                  {department.health === 'HEALTHY'
                    ? '94%'
                    : department.health === 'ATTENTION'
                      ? '78%'
                      : '42%'}
                </strong>
                <small>demo selector</small>
              </div>
              <div>
                <b>Human review</b>
                <strong>unknown</strong>
                <small>no fabricated precision</small>
              </div>
            </div>
          ) : null}
        </div>
        <footer className="drawer__footer">
          <ProvenanceBadge value={snapshot.source.recordProvenance} />{' '}
          <span>
            {snapshot.source.mode === 'LIVE_LOCAL'
              ? 'Lokale Queue-Projektion · keine Runner-Ausführung'
              : 'Local fixture · keine echte Queue'}
          </span>
        </footer>
      </aside>
    </div>
  );
}

export function EmployeeDrawer({
  snapshot,
  personId,
  onClose,
  onRevoke,
}: {
  snapshot: CompanySnapshot;
  personId: string;
  onClose: () => void;
  onRevoke: (id: string) => void;
}) {
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const person = getPerson(snapshot, personId);
  if (!person) return null;
  const manager = person.managerId ? getPerson(snapshot, person.managerId) : undefined;
  const job = getPersonJob(snapshot, person.id);
  const grants = snapshot.grants.filter((grant) => grant.personId === person.id);
  const session = getPersonSession(snapshot, person.id);
  const revoked = session?.status === 'SIMULATED_REVOKED';

  return (
    <div
      className="overlay"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside
        className="drawer employee-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="employee-title"
      >
        <header className="drawer__hero employee-hero">
          <PersonaAvatar name={person.name} nature={person.nature} status={person.status} />
          <div>
            <span className="ui-eyebrow">
              {person.id} · {person.nature === 'PERMANENT_AI' ? 'KI-Persona' : person.nature}
            </span>
            <h2 id="employee-title">{person.name}</h2>
            <p>{person.role}</p>
          </div>
          <button
            className="icon-button"
            onClick={onClose}
            aria-label="Mitarbeiteransicht schließen"
          >
            <X size={19} />
          </button>
        </header>
        <div className="drawer-kpis">
          <span>
            <small>Status</small>
            <b>{person.status}</b>
          </span>
          <span>
            <small>Ceiling</small>
            <b>{person.levelCeiling}</b>
          </span>
          <span>
            <small>Manager</small>
            <b>{manager?.name ?? '—'}</b>
          </span>
        </div>
        <div className="drawer__body employee-body">
          <section className="identity-separation">
            <div>
              <UserRoundCheck size={18} />
              <span>
                <small>Accountable persona</small>
                <strong>{person.name}</strong>
                <em>{person.competency}</em>
              </span>
            </div>
            <ArrowLeftRight size={17} aria-label="ist getrennt von" />
            <div>
              <BriefcaseBusiness size={18} />
              <span>
                <small>Execution capacity</small>
                <strong>{session?.runnerNodeId ?? 'Keine Session'}</strong>
                <em>{session?.tool ?? 'Nicht verbunden'}</em>
              </span>
            </div>
          </section>
          <section className="detail-block">
            <span className="ui-eyebrow">Current assignment</span>
            {job ? (
              <article className="assignment-card">
                <div>
                  <strong>{job.title}</strong>
                  <small>
                    {job.id} · accountable: {person.id}
                  </small>
                </div>
                <StatusPill label={job.status.replace('_', ' ')} tone={statusTone(job.status)} />
              </article>
            ) : (
              <p className="empty-copy">Keine aktive Zuweisung.</p>
            )}
          </section>
          <section className="detail-block">
            <span className="ui-eyebrow">Grants & authority</span>
            <div className="access-band">
              {['A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6'].map((level) => (
                <span
                  key={level}
                  className={person.accessLevels.includes(level as never) ? 'is-active' : ''}
                >
                  {level}
                </span>
              ))}
            </div>
            {grants.map((grant) => (
              <article className="grant-line" key={grant.id}>
                <KeyRound size={15} />
                <div>
                  <strong>{grant.resource}</strong>
                  <small>
                    {grant.permission} · review {grant.expiry}
                  </small>
                </div>
                <StatusPill label={grant.status} tone="green" />
              </article>
            ))}
          </section>
          <section className="detail-block">
            <span className="ui-eyebrow">Runner / model session · separate entity</span>
            {session ? (
              <article className="session-card">
                <div className="session-card__top">
                  <span className={revoked ? 'session-dot is-revoked' : 'session-dot'} />
                  <div>
                    <strong>{session.id}</strong>
                    <small>
                      {session.providerPoolId} · {session.runnerNodeId}
                    </small>
                  </div>
                  <StatusPill
                    label={session.status.replace('SIMULATED_', '')}
                    tone={revoked ? 'red' : 'cyan'}
                  />
                </div>
                <SyntheticBadge compact />
              </article>
            ) : (
              <div className="empty-capacity-detail">
                <p className="empty-copy">Keine Modell-Session im Snapshot.</p>
                <div className="metrics-detail">
                  <div>
                    <b>Runner node</b>
                    <strong>none</strong>
                    <small>keine Ausführung</small>
                  </div>
                  <div>
                    <b>Provider pools</b>
                    <strong>{snapshot.providerPools.length}</strong>
                    <small>nur Metadaten</small>
                  </div>
                  <div>
                    <b>Tool lease</b>
                    <strong>none</strong>
                    <small>fail-closed</small>
                  </div>
                </div>
                <div className="truth-note">
                  <ShieldAlert size={16} />
                  <span>
                    Die verantwortliche KI-Persona bleibt von Runner, Provider-Pool und
                    Modell-Session getrennt.
                  </span>
                </div>
              </div>
            )}
          </section>
          {session ? (
            <section className="danger-zone">
              <div>
                <ShieldAlert size={18} />
                <span>
                  <strong>Demo-Lease widerrufen</strong>
                  <small>
                    Ändert nur den In-Memory-Demo-State und schreibt ein synthetisches Audit-Event.
                  </small>
                </span>
              </div>
              {confirmRevoke && !revoked ? (
                <div className="confirm-box">
                  <p>
                    Folge: Die simulierte Session wird als widerrufen markiert. Keine reale
                    Berechtigung oder Provider-Session wird verändert.
                  </p>
                  <div>
                    <button
                      className="button button--ghost"
                      onClick={() => setConfirmRevoke(false)}
                    >
                      Abbrechen
                    </button>
                    <button
                      className="button button--danger"
                      onClick={() => {
                        onRevoke(person.id);
                        setConfirmRevoke(false);
                      }}
                    >
                      Demo-Widerruf bestätigen
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="button button--danger-outline"
                  disabled={revoked}
                  onClick={() => setConfirmRevoke(true)}
                >
                  {revoked ? (
                    <>
                      <CheckCircle2 size={15} /> Demo-Lease widerrufen
                    </>
                  ) : (
                    'Widerruf simulieren'
                  )}
                </button>
              )}
            </section>
          ) : null}
        </div>
        <footer className="drawer__footer">
          <ProvenanceBadge value={snapshot.source.recordProvenance} />{' '}
          <span>
            {snapshot.source.mode === 'LIVE_LOCAL'
              ? 'Durable metadata · serverseitige Autorisierung · keine externe Aktion'
              : 'Keine persistente Mutation · keine externe Aktion'}
          </span>
        </footer>
      </aside>
    </div>
  );
}

export function CompanyFloorPanel(props: Parameters<typeof CompanyFloor>[0]) {
  return (
    <Panel className="floor-panel">
      <PanelHeader
        eyebrow="Live organization · event-backed preview"
        title="Company Floor"
        action={<span className="panel-hint">8 Räume · 11 Personen</span>}
      />
      <CompanyFloor {...props} />
    </Panel>
  );
}
